import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { boardUrl } from '../CRM/kanban-board/crm-board-link';
import { OWNER_UNASSIGNED } from '../CRM/kanban-board/filters/crm-owner-filter';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { ExportButton } from '@components/ExportButton';
import { datedFileName, downloadSpreadsheet } from '@functions/export-spreadsheet';
import { AgentTable } from './components/agent-table';
import { categoricalPalette, sourceSlot } from './components/chart-palette';
import { FilterPanel } from './components/filter-panel';
import { ForecastTile } from './components/forecast-tile';
import { FunnelColumns } from './components/funnel-columns';
import { OutcomeDonut } from './components/outcome-donut';
import { IdleQueue } from './components/idle-queue';
import { KpiCard } from './components/kpi-card';
import { Panel, PanelStat, PanelState, money, percent, useDashboardColors } from './components/panel';
import { RankedBars } from './components/ranked-bars';
import { SalesGoalsPanel } from './components/sales-goals-panel';
import { VolumeChart } from './components/volume-chart';
import { dashboardExportSheets } from './dashboard-export';
import {
    DashboardFilterState,
    change,
    displayDay,
    filterQuery,
    initialFilters,
    winRate,
} from './dashboard-filters';
import { DashboardService } from './services/dashboard.service';
import {
    DashboardFilterOptions,
    DashboardIdle,
    DashboardSummary,
    ForecastReport,
    FunnelReport,
} from './services/responses/dashboard.response';

/// Linhas nos rankings: mais que isso vira lista, e a completa está na tabela.
const TOP = 8;

export const Dashboard = () => {
    const { t, i18n } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = useDashboardColors();
    const palette = categoricalPalette(controller.darkMode);
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const [filters, setFilters] = useState<DashboardFilterState>(() => initialFilters());
    const [options, setOptions] = useState<DashboardFilterOptions | undefined>();
    const [summary, setSummary] = useState<DashboardSummary | undefined>();
    const [idle, setIdle] = useState<DashboardIdle | undefined>();
    const [funnel, setFunnel] = useState<FunnelReport | undefined>();
    const [forecast, setForecast] = useState<ForecastReport | undefined>();
    const [updatedAt, setUpdatedAt] = useState<Date | undefined>();
    const [agentsOpen, setAgentsOpen] = useState(false);
    const [goalsRefresh, setGoalsRefresh] = useState(0);

    // Troca rápida de filtro dispara pedidos em sequência; a resposta velha não sobrescreve a nova.
    const requestRef = useRef(0);

    useEffect(() => {
        new DashboardService()
            .filters()
            .then((reply) => setOptions(reply.data.data))
            .catch(() => setOptions({ agents: [], sources: [], campaigns: [], columns: [] }));
    }, []);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        const current = () => request === requestRef.current;
        const query = filterQuery(filters);
        const service = new DashboardService();

        setSummary(undefined);
        setIdle(undefined);
        setFunnel(undefined);
        setForecast(undefined);

        // Cada bloco chega por conta própria: o funil percorre o histórico e não segura os de cima.
        const summaryCall = service.summary(query)
            .then((reply) => current() && setSummary(reply.data.data))
            .catch((err) => current() && showAlert(apiErrorMessage(err), 'error', 3000));
        const idleCall = service.idle(query)
            .then((reply) => current() && setIdle(reply.data.data))
            .catch(() => current() && setIdle({ open: 0, stale: 0, byOwner: [], byColumn: [], agingDays: 3, staleDays: 7 }));
        const funnelCall = service.funnel(query)
            .then((reply) => current() && setFunnel(reply.data.data))
            .catch(() => current() && setFunnel({ from: filters.from, to: filters.to, entered: 0, stages: [], lossReasons: [], outcomes: { open: 0, won: 0, lost: 0 } }));
        const forecastCall = service.forecast(query)
            .then((reply) => current() && setForecast(reply.data.data))
            .catch(() => current() && setForecast({ months: [], overdueTotal: 0, overdueCount: 0 }));

        await Promise.all([summaryCall, idleCall, funnelCall, forecastCall]);

        if (current()) setUpdatedAt(new Date());
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const patch = (next: Partial<DashboardFilterState>) => setFilters((old) => ({ ...old, ...next }));

    // Clicar de novo no que já está filtrado desfaz o filtro.
    const toggle = (key: keyof DashboardFilterState, id: string) =>
        setFilters((old) => ({ ...old, [key]: old[key] === id ? '' : id }));

    const totals = summary?.totals;
    const previous = summary?.previous;
    const compareLabel = summary
        ? t('painel.vs', { de: displayDay(summary.previousFrom), ate: displayDay(summary.previousTo) })
        : undefined;
    const rate = totals ? winRate(totals.won, totals.lost) : null;

    const lossRows = useMemo(
        () => (funnel?.lossReasons ?? []).slice(0, TOP).map((reason) => ({
            key: reason.reasonId ?? 'sem-motivo',
            label: reason.name,
            // Meio painel corta motivo longo com reticências; o nome inteiro fica no hover.
            tooltip: reason.name,
            sublabel: reason.value > 0 ? money(reason.value) : undefined,
            value: reason.count,
            // "Sem motivo" não é motivo: fica apagado para não competir com os que explicam algo.
            color: reason.reasonId == null ? cc.textMuted : cc.eventDanger,
            muted: reason.reasonId == null,
        })),
        [funnel, cc]);

    const lostTotal = (funnel?.lossReasons ?? []).reduce((sum, reason) => sum + reason.count, 0);
    const lostValue = (funnel?.lossReasons ?? []).reduce((sum, reason) => sum + reason.value, 0);

    const updatedLabel = updatedAt
        ? t('painel.atualizadoEm', {
            dia: updatedAt.toLocaleDateString(i18n.language),
            hora: updatedAt.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
        })
        : t('painel.atualizando');

    const twoColumns = { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('crmDashboard.titulo')} />

            <Box sx={{ background: cc.pageBg, borderRadius: 3, p: 2, display: 'grid', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ color: cc.textSecondary, fontSize: '0.85rem', flex: 1, minWidth: 220 }}>
                        {t('painel.subtitulo')}
                    </Typography>
                    <Typography sx={{ color: cc.textMuted, fontSize: '0.78rem' }}>{updatedLabel}</Typography>
                    <Tooltip title={t('painel.atualizar')}>
                        <span>
                            <IconButton
                                onClick={() => {
                                    load();
                                    setGoalsRefresh((old) => old + 1);
                                }}
                                disabled={!updatedAt}
                                aria-label={t('painel.atualizar')}
                                sx={{
                                    height: 43,
                                    width: 43,
                                    bgcolor: cc.surfaceBg,
                                    border: `1px solid ${cc.border}`,
                                    borderRadius: '9px',
                                    color: cc.textSecondary,
                                }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <ExportButton
                        title={t('exportar.painel')}
                        disabled={!summary}
                        onClick={() => summary && downloadSpreadsheet(
                            datedFileName('painel'), dashboardExportSheets(summary, funnel, idle, t))}
                    />
                </Box>

                <FilterPanel value={filters} options={options} onChange={setFilters} />

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                    <KpiCard
                        label={t('painel.leadsNovos')}
                        value={totals?.leads.toLocaleString('pt-BR')}
                        unit={t('painel.leads', { count: totals?.leads ?? 0 })}
                        hint={t('painel.leadsNovosAjuda')}
                        change={totals && previous ? change(totals.leads, previous.leads) : undefined}
                        compareLabel={compareLabel}
                    />
                    <KpiCard
                        label={t('painel.vendas')}
                        value={totals?.won.toLocaleString('pt-BR')}
                        unit={t('painel.fechadas', { count: totals?.won ?? 0 })}
                        hint={totals
                            ? rate === null ? t('painel.semEncerrados') : t('painel.taxaGanho', { taxa: percent(rate), count: totals.lost })
                            : undefined}
                        change={totals && previous ? change(totals.won, previous.won) : undefined}
                        compareLabel={compareLabel}
                    />
                    <KpiCard
                        label={t('crmDashboard.receita')}
                        value={totals ? money(totals.wonValue) : undefined}
                        // Ganho sem valor confirmado entra pela estimativa do lead; senão a receita não bateria.
                        hint={totals
                            ? [
                                t('crmDashboard.receitaConfirmada', { confirmados: totals.wonValueConfirmed, count: totals.won }),
                                ...(totals.wonValueConfirmed < totals.won ? [t('crmDashboard.restoEstimativa')] : []),
                            ].join(' · ')
                            : undefined}
                        change={totals && previous ? change(totals.wonValue, previous.wonValue) : undefined}
                        compareLabel={compareLabel}
                    />
                </Box>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                    <KpiCard
                        label={t('painel.emNegociacao')}
                        value={summary ? money(summary.openValue) : undefined}
                        hint={summary ? t('painel.emNegociacaoAjuda', { total: summary.openCount }) : undefined}
                    />
                    <KpiCard
                        label={t('crmDashboard.semDono')}
                        value={summary?.unassignedOpen.toLocaleString('pt-BR')}
                        unit={t('painel.leads', { count: summary?.unassignedOpen ?? 0 })}
                        hint={t('painel.semDonoAjuda')}
                        attention={(summary?.unassignedOpen ?? 0) > 0}
                        linkLabel={summary?.unassignedOpen ? t('painel.verSemDono') : undefined}
                        onLink={() => navigate(boardUrl({ responsavel: OWNER_UNASSIGNED }))}
                    />
                    <ForecastTile data={forecast} />
                </Box>

                <SalesGoalsPanel periodEnd={filters.to} refreshKey={goalsRefresh} />

                <Box sx={twoColumns}>
                    <VolumeChart
                        data={summary}
                        onPickDay={(day) => patch({ preset: 'custom', from: day, to: day })}
                    />
                    <IdleQueue
                        data={idle}
                        onPickOwner={(id) => navigate(boardUrl({ responsavel: id ?? OWNER_UNASSIGNED }))}
                        onPickColumn={(id, boardId) => navigate(boardUrl({ quadro: boardId, coluna: id }))}
                    />
                </Box>

                <Box sx={twoColumns}>
                    <FunnelColumns
                        data={funnel}
                        selectedColumn={filters.column}
                        onPickColumn={(id) => toggle('column', id)}
                    />
                    {/* Metade cada: os motivos e a rosca falam dos mesmos leads do período. */}
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                        <Panel
                            title={t('crmDashboard.porQuePerdeu')}
                            subtitle={t('painel.perdasAjuda')}
                            stats={funnel && lostTotal > 0 && (
                                <>
                                    <PanelStat label={t('painel.perdidos')} value={lostTotal} alert />
                                    {lostValue > 0 && <PanelStat label={t('painel.valorPerdido')} value={money(lostValue)} />}
                                </>
                            )}
                        >
                            {!funnel || lossRows.length === 0
                                ? <PanelState loading={!funnel} empty={t('crmDashboard.nenhumaPerda')} />
                                : <RankedBars rows={lossRows} total={lostTotal} stacked />}
                        </Panel>
                        <OutcomeDonut data={funnel} />
                    </Box>
                </Box>

                <Box sx={twoColumns}>
                    <Panel
                        title={t('crmDashboard.origem')}
                        subtitle={t('painel.origemAjuda')}
                        footer={summary?.bySource.length ? t('painel.cliqueFiltrar') : undefined}
                    >
                        {!summary || summary.bySource.length === 0 ? (
                            <PanelState loading={!summary} empty={t('crmDashboard.semDados')} />
                        ) : (
                            <RankedBars
                                labelWidth={150}
                                rows={summary.bySource.slice(0, TOP).map((slice) => ({
                                    key: String(slice.source),
                                    label: slice.label,
                                    value: slice.count,
                                    color: palette[sourceSlot(slice.source)],
                                    selected: filters.source === String(slice.source),
                                    chip: (
                                        <Box
                                            aria-hidden
                                            sx={{ width: 10, height: 10, borderRadius: '3px', flexShrink: 0, background: palette[sourceSlot(slice.source)] }}
                                        />
                                    ),
                                }))}
                                total={totals?.leads}
                                onSelect={(row) => toggle('source', row.key)}
                            />
                        )}
                    </Panel>

                    <Panel
                        title={t('painel.porResponsavel')}
                        subtitle={t('painel.porResponsavelAjuda')}
                        action={
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!summary?.byAgent.length}
                                onClick={() => setAgentsOpen(true)}
                                sx={{ textTransform: 'none', color: cc.accent, borderColor: cc.border, flexShrink: 0 }}
                            >
                                {t('painel.verTabela')}
                            </Button>
                        }
                        footer={summary?.byAgent.length ? t('painel.cliqueFiltrar') : undefined}
                    >
                        {!summary || summary.byAgent.length === 0 ? (
                            <PanelState loading={!summary} empty={t('crmDashboard.semAtendente')} />
                        ) : (
                            <RankedBars
                                labelWidth={170}
                                rows={summary.byAgent.slice(0, TOP).map((agent) => ({
                                    key: agent.userId,
                                    label: agent.name,
                                    sublabel: `${t('painel.ganhosQtd', { count: agent.won })} · ${t(
                                        'painel.acoesConcluidasQtd',
                                        { count: agent.schedulesCompleted }
                                    )}`,
                                    value: agent.leads,
                                    color: cc.accent,
                                    selected: filters.owner === agent.userId,
                                }))}
                                total={totals?.leads}
                                onSelect={(row) => toggle('owner', row.key)}
                            />
                        )}
                    </Panel>
                </Box>
            </Box>

            <Dialog
                open={agentsOpen}
                onClose={() => setAgentsOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { background: cc.cardBg, backgroundImage: 'none' } }}
            >
                <DialogContent sx={{ p: 1.5 }}>
                    <AgentTable data={summary?.byAgent} />
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
};
